// PerformanceTab — the "Performance" holdings view: a trailing-returns table
// (1Mo→10Yr) per holding (reusable DataTable + PerfNum), closed by an
// allocation-weighted portfolio row. A managed model is one row (its own return
// where the wire states one) that expands to its member funds. Clicking a fund row
// opens the Fund Detail overlay. Source of truth: portfolio3.jsx PerformanceTab.
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, PerfNum } from '../../../reusable';
import { formatDate } from '../../../../utils/format';
import { cx } from '../../../../utils/cx';
import { holdingLeaves } from '../portfolio.adapters';
import { treeOrFlat, flattenRows } from '../portfolio.models';
import { ModelToggle, ModelTag, useModelExpand } from './ModelRow';
import './holdings-table.css';

// Trailing-return periods. Keys drive the weighted calc + data lookup; labels
// resolve via i18n (translation.json portfolio.perf.p_<key>).
const PERIODS = ['oneMo', 'threeMo', 'ytd', 'oneYr', 'threeYr', 'fiveYr', 'tenYr'];

// A fund with no return in any period is left out, as Classic does (FM-3094).
export const hasReturns = (f) => PERIODS.some((k) => f[k] != null);
const withReturns = (items) => items
  .map((it) => (it.type === 'model' ? { ...it, children: (it.children || []).filter(hasReturns) } : it))
  .filter((it) => hasReturns(it) || it.children?.length);

const FundName = ({ value, row, open, onToggle, t, ownAsOf }) => {
  if (row._model) {
    return (
      <span className="holdings-fund holdings-fund--model">
        <ModelToggle open={open} name={row.name} onToggle={onToggle} t={t} />
        <span className="holdings-fund__sw" style={{ background: row.color }} />
        <span className="holdings-fund__nm">{value}</span>
        <ModelTag t={t} />
      </span>
    );
  }
  return (
    <span className={cx('holdings-fund', row._total && 'holdings-fund--total', row._child && 'holdings-fund--child')}>
      {row.color && <span className="holdings-fund__sw" style={{ background: row.color }} />}
      <span className="holdings-fund__nm">{value}</span>
      {ownAsOf && <span className="holdings-fund__asof">{t('portfolio.perf.fundAsOf', { date: ownAsOf })}</span>}
    </span>
  );
};
FundName.propTypes = {
  value: PropTypes.string,
  row: PropTypes.object,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  t: PropTypes.func.isRequired,
  /** The fund's own returns date, when it differs from the table's "Returns as of" date. */
  ownAsOf: PropTypes.string,
};

export function PerformanceTab({ funds = [], tree, asOf, onPickFund, onPickModel }) {
  const { t } = useTranslation();
  const { isOpen, toggle } = useModelExpand();
  const items = useMemo(() => withReturns(treeOrFlat(tree, funds)), [tree, funds]);

  // Weighted over the funds actually held — a model contributes its members. Each period
  // is weighted over the funds that HAVE that return, since a fund with none is no longer
  // listed to explain the gap (FM-3094); no fund with data → a dash.
  const weighted = useMemo(() => {
    const leaves = holdingLeaves(items);
    const w = {};
    PERIODS.forEach((key) => {
      const withData = leaves.filter((f) => f[key] != null);
      const share = withData.reduce((s, f) => s + (f.pct || 0), 0);
      w[key] = share ? withData.reduce((s, f) => s + f[key] * (f.pct / share), 0) : null;
    });
    return w;
  }, [items]);

  // Fund return dates can differ by a quarter or more; a fund behind the caption's date names its own (FM-3094).
  const captionDate = asOf ? formatDate(asOf) : null;
  const ownAsOf = (row) => {
    const own = row.perfAsOf ? formatDate(row.perfAsOf) : null;
    return own && own !== captionDate ? own : undefined;
  };

  const totalRow = { id: '__total', key: '__total', _total: true, name: t('portfolio.perf.weighted'), ticker: '', color: null, ...weighted };
  const rows = [...flattenRows(items, isOpen), totalRow];

  const columns = [
    {
      key: 'name',
      label: t('portfolio.perf.colFund'),
      sortable: false,
      render: (value, row) => (
        <FundName value={value} row={row} open={!!row._model && isOpen(row.key)} onToggle={() => toggle(row.key)} t={t} ownAsOf={ownAsOf(row)} />
      ),
    },
    {
      key: 'ticker',
      label: t('portfolio.perf.colTicker'),
      sortable: false,
      render: (v, row) => (row._model ? '—' : v ? <span className="holdings-tkr">{v}</span> : '—'),
    },
    ...PERIODS.map((key) => ({
      key,
      label: t(`portfolio.perf.p_${key}`),
      numeric: true,
      sortable: false,
      render: (v) => (v == null ? '—' : <PerfNum value={v} digits={2} />),
    })),
  ];

  return (
    <div className="performance-tab">
      <DataTable
        className="holdings-table holdings-table--perf data-table--no-collapse"
        columns={columns}
        rows={rows}
        rowKey={(r, i) => (r._child ? `${r._modelKey}:${r.ticker}` : r.key || r.ticker || r.id || i)}
        rowClassName={(r) =>
          cx(
            r._total && 'holdings-table__total-row',
            r._model && 'holdings-table__model-row',
            r._child && 'holdings-table__child-row',
          ) || undefined
        }
        onRowClick={(r) => {
          if (r._total) return;
          if (r._model) (onPickModel ? onPickModel(r.key) : toggle(r.key));
          else onPickFund?.(r.ticker);
        }}
      />
      <p className="holdings-table__note">
        {asOf && <>{t('portfolio.perf.returnsAsOf', { date: formatDate(asOf) })}</>}
        {t('portfolio.perf.note')}
      </p>
    </div>
  );
}

PerformanceTab.propTypes = {
  funds: PropTypes.array,
  /** Model-aware holdings list; falls back to one row per fund when absent. */
  tree: PropTypes.array,
  asOf: PropTypes.instanceOf(Date),
  onPickFund: PropTypes.func,
  /** (modelKey) — a model row was clicked; absent, the row toggles its funds instead. */
  onPickModel: PropTypes.func,
};

export default PerformanceTab;
